import { isDefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from "@mui/material";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { type UseModalState } from "@src/lib/ui-react";
import { isAxiosError } from "axios";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { useGetQualifications } from "../Admin/Qualifications/api/useGetQualifications";
import { useGetWorkplaceTypes } from "../Admin/WorkplaceTypes/api/useGetWorkplaceTypes";
import { useRequirementMappingApis } from "./api/useRequirementMappingApis";
import { DomainSelectField } from "./FormFields/DomainSelectField";
import { IsOrientationRequirementField } from "./FormFields/IsOrientationRequirementField";
import { LevelConfirmationField } from "./FormFields/LevelConfirmationField";
import { LevelSelectField } from "./FormFields/LevelSelectField";
import { MappingDescription } from "./FormFields/MappingDescription";
import { QualificationsField } from "./FormFields/QualificationsField";
import { RequiredBySelectField } from "./FormFields/RequiredBySelectField";
import { RequiredForBookingField } from "./FormFields/RequiredForBookingField";
import { RequirementSelectField } from "./FormFields/RequirementSelectField";
import { WorkplaceTypesField } from "./FormFields/WorkplaceTypesField";
import {
  type CreateRequirementMappingDto,
  Domain,
  MappingLevel,
  REQUIREMENT_MAPPING_RESOURCE,
  REQUIREMENT_RESOURCE,
  type RequirementMapping,
  requirementMappingFormSchema,
  type RequirementMappingFormValues,
  requiresLevelConfirmation,
} from "./types";

interface RequirementMappingDialogProps {
  modalState: UseModalState;
  requirementId?: string;
  hcfId?: string;
  title?: string;
  requirementMapping?: RequirementMapping;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export function RequirementMappingDialog(props: RequirementMappingDialogProps) {
  const {
    modalState,
    hcfId,
    title = "Add Requirement Mapping",
    requirementMapping,
    onSuccess,
    requirementId,
    onCancel,
  } = props;
  const {
    createRequirementMapping,
    isCreatingRequirementMapping,
    updateRequirementMapping,
    isUpdatingRequirementMapping,
  } = useRequirementMappingApis();
  const { data: workplaceTypesData, isLoading: isLoadingWorkplaceTypes } = useGetWorkplaceTypes();
  const { data: qualificationsData, isLoading: isLoadingQualifications } = useGetQualifications();
  const qualificationOptions = qualificationsData?.data.map(({ attributes }) => attributes.name);
  const workplaceTypeOptions = workplaceTypesData?.data.map(({ attributes }) => attributes.name);
  const existingMappingAttributes = requirementMapping?.attributes;
  const mode = isDefined(requirementMapping?.id) ? "edit" : "create";

  const existingFormValues = {
    requirementId: requirementMapping?.relationships.requirement.data.id,
    domain: existingMappingAttributes?.domain ?? Domain.FLEX,
    level: existingMappingAttributes?.level,
    qualifications: existingMappingAttributes?.qualifications ?? [],
    allWorkplaceTypes:
      existingMappingAttributes?.workplaceTypes.length === workplaceTypeOptions?.length,
    workplaceTypes: existingMappingAttributes?.workplaceTypes ?? [],
    requiredBy: existingMappingAttributes?.requiredBy,
    requiredByHCFs:
      existingMappingAttributes?.level === MappingLevel.HCF
        ? existingMappingAttributes?.requiredBy.map((id) => ({ id, name: id }))
        : [],
    requiredForBooking: existingMappingAttributes?.requiredForBooking,
    isOrientationRequirement: existingMappingAttributes?.isOrientationRequirement ?? false,
    levelConfirmation: "",
  };

  const defaultValues = {
    requirementId,
    level: hcfId ? MappingLevel.HCF : MappingLevel.GLOBAL,
    domain: Domain.FLEX,
    qualifications: [],
    allWorkplaceTypes: true,
    workplaceTypes: [],
    requiredBy: [],
    requiredByHCFs: hcfId ? [{ id: hcfId, name: hcfId }] : [],
    requiredForBooking: true,
    isOrientationRequirement: false,
    levelConfirmation: "",
  };

  const methods = useForm<RequirementMappingFormValues>({
    resolver: zodResolver(requirementMappingFormSchema),
    defaultValues: mode === "edit" ? existingFormValues : defaultValues,
  });

  const { reset, handleSubmit, control, setValue, watch } = methods;

  const levelWatcher = watch("level");

  // Reset requiredBy when level changes
  useEffect(() => {
    if (existingMappingAttributes?.level === levelWatcher) {
      setValue("requiredBy", existingMappingAttributes?.requiredBy);
    } else {
      setValue("requiredBy", []);
    }
    if (levelWatcher !== MappingLevel.STATE) {
      setValue("isOrientationRequirement", false);
    } else if (existingMappingAttributes?.level === MappingLevel.STATE) {
      setValue("isOrientationRequirement", existingMappingAttributes.isOrientationRequirement);
    }
  }, [levelWatcher, setValue, existingMappingAttributes]);

  const closeModal = () => {
    reset();
    modalState.closeModal();
    onCancel?.();
  };

  const createMappingPayload = (
    values: RequirementMappingFormValues
  ): CreateRequirementMappingDto => {
    let requiredBy: string[] = [];
    // FIXME: Automatically disabled during ESLint config update
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (true) {
      case isDefined(hcfId): {
        requiredBy = [hcfId];
        break;
      }

      case values.level === MappingLevel.GLOBAL: {
        requiredBy = ["Clipboard Health"];
        break;
      }

      case values.level === MappingLevel.HCF: {
        requiredBy = values.requiredByHCFs?.map((hcf) => hcf.id) ?? [];
        break;
      }

      default: {
        requiredBy = values.requiredBy ?? [];
      }
    }

    const { qualifications } = values;

    const {
      level,
      requiredForBooking,
      isOrientationRequirement,
      domain,
      requirementId,
      allWorkplaceTypes,
    } = values;
    let { workplaceTypes } = values;
    if (allWorkplaceTypes) {
      workplaceTypes = workplaceTypeOptions ?? [];
    }

    return {
      data: {
        type: REQUIREMENT_MAPPING_RESOURCE,
        attributes: {
          level,
          qualifications,
          workplaceTypes,
          requiredBy,
          requiredForBooking,
          domain,
          isOrientationRequirement,
        },
        relationships: {
          requirement: {
            data: {
              id: requirementId,
              type: REQUIREMENT_RESOURCE,
            },
          },
        },
      },
    };
  };

  const onSubmit = async (formValues: RequirementMappingFormValues) => {
    try {
      const payload = createMappingPayload(formValues);
      await (isDefined(requirementMapping?.id)
        ? updateRequirementMapping({
            ...payload,
            data: {
              ...payload.data,
              id: requirementMapping.id,
            },
          })
        : createRequirementMapping(payload));
      closeModal();
    } catch (error: unknown) {
      let errorMessage = "Something went wrong while creating the requirement mapping";
      if (isAxiosError(error)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        errorMessage = error?.response?.data?.message ?? errorMessage;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      showErrorToast(errorMessage);
    } finally {
      onSuccess?.();
    }
  };

  return (
    <FormProvider {...methods}>
      <Dialog fullWidth open={modalState.modalIsOpen} maxWidth="md" onClose={closeModal}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isCreatingRequirementMapping || isUpdatingRequirementMapping}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <DialogTitle>
          {title}
          <IconButton
            aria-label="close"
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            onClick={closeModal}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Stack spacing={1}>
              <MappingDescription control={control} />
              <DomainSelectField control={control} disabled={mode === "edit"} setValue={setValue} />
              <RequirementSelectField
                control={control}
                requirementId={
                  requirementId ?? requirementMapping?.relationships.requirement.data.id
                }
              />
              <LevelSelectField control={control} hcfId={hcfId} setValue={setValue} />
              <RequiredBySelectField control={control} hcfId={hcfId} />
              {levelWatcher !== MappingLevel.HCF && (
                <WorkplaceTypesField
                  control={control}
                  workplaceTypeOptions={workplaceTypeOptions ?? []}
                  isLoading={isLoadingWorkplaceTypes}
                />
              )}
              <QualificationsField
                control={control}
                qualificationOptions={qualificationOptions ?? []}
                isLoading={isLoadingQualifications}
              />
              <RequiredForBookingField control={control} />
              {levelWatcher === MappingLevel.STATE && (
                <IsOrientationRequirementField control={control} />
              )}
              {requiresLevelConfirmation(levelWatcher) && (
                <LevelConfirmationField control={control} />
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button type="button" variant="outlined" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                {mode === "create" ? "Create" : "Update"}
              </Button>
            </Stack>
          </DialogActions>
        </Box>
      </Dialog>
    </FormProvider>
  );
}
